import { EditorState, ContentState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import React, { useEffect, useRef, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { Entity } from '@debbie/common/dist/index.js';
import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { EditorToolbarOptions } from '@debbie/common/dist/constants.js';
import { SecondaryEmailType, getContactPerson, } from '@debbie/common/dist/customers/index.js';
import { IntentType } from '@debbie/common/dist/intents/index.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { getMentionSuggestions } from '@debbie/common/dist/templates/suggestions.js';
import { cancelDebounce, debounce } from '@debbie/common/dist/util/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { selectCaseById } from '@debbie/cortex/dist/cases/selectors.js';
import { selectCustomerById } from '@debbie/cortex/dist/customers/selectors.js';
import { selectEmail, selectUploadedFiles, } from '@debbie/cortex/dist/newInteractions/selectors.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
import { Button, Modal, Input, File, FileDropzone, Intent } from '..//index.js';
import THEME from '../../theme.js';
import { encodeText, getSubjectContent, parseText, } from '../../utils/templates.js';
import { Eye, EyeOff, PlusCircle, PlusSquare } from '../Icons/index.js';
import HelpCircle from '../Icons/HelpCircle.js';
import EmailPreview from './EmailPreview.js';
import MentionExplanation from './MentionExplanation.js';
import TemplateSearch from './TemplateSearch.js';
import { fromHtml, toHtml } from './utils.js';
import { AddFromTemplate } from './/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { useEmailServer } from '@debbie/cortex/dist/email-servers/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { filterPropertyTypes } from '@debbie/common/dist/properties/index.js';
const EmailInteraction = (props) => {
    const editorRef = useRef(null);
    useEffect(() => {
        if (editorRef && editorRef.current) {
            // @ts-ignore
            editorRef.current?.focusEditor();
        }
    }, [editorRef]);
    const singleCase = useSelector((s) => selectCaseById(s, props.referenceId));
    const customer = useSelector((s) => singleCase && selectCustomerById(s, singleCase.customerId));
    const creditorId = singleCase?.creditorId ||
        (props.referenceType === Entity.Creditor && props.referenceId);
    const tenant = useSelector(selectTenant);
    const creditorLoadable = useCreditor(creditorId || undefined);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const documentsLoadable = useDocumentsByReference(props.referenceType === Entity.Case ? props.referenceId : undefined);
    const documents = h.hasData(documentsLoadable) ? documentsLoadable.data : null;
    const propertyTypesLoadable = usePropertyTypes();
    const propertyTypes = h.hasData(propertyTypesLoadable)
        ? propertyTypesLoadable.data
        : undefined;
    const [emailPreviewModalOpen, setEmailPreviewModalOpen] = useState(false);
    const [emailTemplateAdderOpen, setEmailTemplateAdderOpen] = useState(false);
    const dropzoneRef = useRef(null);
    const [mentionExplanationModalOpen, setMentionExplanationModalOpen] = useState(false);
    const [emailToInput, setEmailToInput] = useState(props.emailTo);
    const [emailCCInput, setEmailCCInput] = useState('');
    const [emailBCCInput, setEmailBCCInput] = useState('');
    const [ccAndBccOpen, setCcAndBccOpen] = useState(false);
    const [visibleToClients, setVisibleToClients] = useState(true);
    const lang = useSelector((s) => s.lang);
    const email = useSelector((s) => selectEmail(s, props.referenceId));
    const [clean, setClean] = useState(true);
    const [reset, setReset] = useState(false);
    const [content, setContent] = useState(props.defaultContent ? fromHtml(props.defaultContent) : email.editorState);
    const [subject, setSubject] = useState(props.defaultSubject
        ? EditorState.createWithContent(ContentState.createFromText(props.defaultSubject))
        : email.subjectEditorState);
    useEffect(() => {
        if (reset) {
            setContent(props.defaultContent
                ? fromHtml(props.defaultContent)
                : email.editorState);
            setSubject(props.defaultSubject
                ? EditorState.createWithContent(ContentState.createFromText(props.defaultSubject))
                : email.subjectEditorState);
            setReset(false);
            setClean(true);
        }
    }, [email.editorState, email.subjectEditorState]);
    const uploadedFiles = useSelector((s) => selectUploadedFiles(s, props.referenceId));
    const mentionSuggestions = getMentionSuggestions(lang, TemplateType.EMAIL, propertyTypes
        ? filterPropertyTypes(propertyTypes, Entity.Case, 'case_groups', singleCase?.caseGroup?.id || null)
        : undefined, tenant);
    const collectorId = props.referenceType === Entity.Creditor
        ? tenant?.collectorIdentityId
        : singleCase &&
            creditor &&
            getCollectorOnCase(singleCase.type, singleCase.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId);
    const collectorLoadable = useCollector(collectorId || undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const emailServerLoadable = useEmailServer(collector?.emailServerId);
    const emailServer = h.hasData(emailServerLoadable)
        ? emailServerLoadable.data
        : null;
    useEffect(() => {
        if (customer && props.isDebtor) {
            const contact = getContactPerson(customer);
            if (contact) {
                const secondaryEmails = (contact.details.secondaryEmails || []).filter((email) => email.enabled);
                if (secondaryEmails.length > 0) {
                    setCcAndBccOpen(true);
                    const toEmails = secondaryEmails.filter((email) => email.type === SecondaryEmailType.To);
                    if (toEmails.length) {
                        setEmailToInput(`${emailToInput};${toEmails.map(({ email }) => email).join(';')}`);
                    }
                    const ccEmails = secondaryEmails.filter((email) => email.type === SecondaryEmailType.CC);
                    if (ccEmails.length) {
                        setEmailCCInput(ccEmails.map(({ email }) => email).join(';'));
                    }
                    const bccEmails = secondaryEmails.filter((email) => email.type === SecondaryEmailType.BCC);
                    if (bccEmails.length) {
                        setEmailBCCInput(bccEmails.map(({ email }) => email).join(';'));
                    }
                }
            }
        }
    }, [customer]);
    useEffect(() => {
        if (props.defaultSubject) {
            return;
        }
        if (Object.keys(email).length > 0 && clean) {
            let subject = '';
            if (props.referenceType === Entity.Case) {
                const genericSubject = encodeText('{{genericEmailSubject}}', mentionSuggestions);
                const courtSubject = `Sag hos {{collector.name}}: {{tribunal.latestReferenceId}} [j.nr.: {{journalNumber}}]`; // TODO: Lang
                subject = props.isCourt ? courtSubject : genericSubject;
            }
            else if (props.referenceType === Entity.Creditor) {
                if (collector) {
                    // TODO: Lang
                    subject = `Besked fra ${collector.name}`;
                }
            }
            if (subject) {
                const content = EditorState.createWithContent(ContentState.createFromBlockArray(htmlToDraft.default(encodeText(subject, mentionSuggestions))
                    .contentBlocks));
                setSubject(content);
                c.newInteractions.changeEmailSubjectEditorState(props.referenceId, content);
            }
            setClean(false);
        }
    }, [props.isCourt, email, collector, clean]);
    const hasSubject = () => {
        return !!subject;
    };
    const hasBody = () => {
        return !!(content &&
            content.getCurrentContent() &&
            content.getCurrentContent().getPlainText());
    };
    const toggleAddTemplateModal = () => {
        setEmailTemplateAdderOpen(!emailTemplateAdderOpen);
    };
    const toggleEmailPreviewModal = () => {
        setEmailPreviewModalOpen(!emailPreviewModalOpen);
    };
    const toggleMentionExplanation = () => {
        setMentionExplanationModalOpen(!mentionExplanationModalOpen);
    };
    const sendEmail = async () => {
        if (emailServer && email && emailToInput) {
            cancelDebounce('email-subject');
            cancelDebounce('email-content');
            setReset(true);
            await c.newInteractions.sendEmail(props.referenceId, props.referenceType, email.templateId, {
                server: emailServer.id,
                subject: getSubjectContent(parseText(toHtml(subject), mentionSuggestions)),
                from: `${emailServer.fromName} <${emailServer.fromEmail}>`,
                replyTo: collector?.replyTo,
                body: parseText(toHtml(content), mentionSuggestions),
                html: parseText(toHtml(content), mentionSuggestions),
                to: emailToInput,
                cc: emailCCInput,
                bcc: emailBCCInput,
                inReplyTo: props.inReplyTo ?? undefined,
                references: props.references ?? undefined,
            }, (uploadedFiles || []).map((f) => f.fileId), visibleToClients ? 'client' : 'caseworker', email.intent);
            setEmailToInput(props.emailTo);
            setEmailCCInput('');
            setEmailBCCInput('');
            if (props.onSent) {
                props.onSent();
            }
        }
    };
    const changeContent = (state, time) => {
        setContent(state);
        debounce('email-content', () => {
            c.newInteractions.changeEditorState(props.referenceId, 'email', state);
        }, time);
    };
    const changeSubject = (state, time) => {
        setSubject(state);
        debounce('email-subject', () => {
            c.newInteractions.changeEmailSubjectEditorState(props.referenceId, state);
        }, time);
    };
    const documentsVisibleForDebtorToAdd = documents
        ? documents.filter((document) => document.minAccessRole === 'debtor' &&
            !uploadedFiles.find((uploadedFile) => uploadedFile.fileId === document.file?.fileId))
        : null;
    return (React.createElement(React.Fragment, null,
        React.createElement(FileDropzone, { onChange: (file) => c.newInteractions.addFiles(props.referenceId, [file]), dropzoneRef: dropzoneRef },
            React.createElement("div", { style: { position: 'relative', zIndex: 1 } },
                React.createElement(HelpCircle, { onClick: toggleMentionExplanation, style: {
                        cursor: 'pointer',
                        float: 'right',
                        marginRight: '5px',
                        color: THEME.COLOR_ACCENT,
                        position: 'absolute',
                        right: 0,
                    } })),
            React.createElement("p", { style: { fontSize: '0.8em' } }, emailServer
                ? `${emailServer.fromName} <${emailServer.fromEmail}>`
                : ''),
            email && singleCase && customer && (React.createElement(TemplateSearch, { interaction: email, templateType: TemplateType.EMAIL, case: singleCase, customer: customer, onChange: (state, subject) => {
                    setContent(state);
                    if (subject != null) {
                        if (props.defaultSubject == null) {
                            setSubject(subject);
                        }
                    }
                } })),
            React.createElement(Input, { disabled: typeof props.emailTo !== 'undefined' && !props.emailToEditable, placeholder: lang.interactions.to, value: emailToInput || '', onChange: (e) => {
                    setEmailToInput(e.target.value);
                } }),
            ccAndBccOpen ? (React.createElement(React.Fragment, null,
                React.createElement(Input, { placeholder: "CC", value: emailCCInput, onChange: (e) => {
                        setEmailCCInput(e.target.value);
                    } }),
                React.createElement(Input, { placeholder: "BCC", value: emailBCCInput, onChange: (e) => {
                        setEmailBCCInput(e.target.value);
                    } }))) : (React.createElement("div", { style: {
                    position: 'relative',
                    marginBottom: '5px',
                } },
                React.createElement("div", { style: {
                        position: 'absolute',
                        fontWeight: 900,
                        fontSize: '0.7em',
                        cursor: 'pointer',
                        color: THEME.COLOR_ACCENT,
                        top: '-19px',
                        right: '0px',
                    }, onClick: () => setCcAndBccOpen(!ccAndBccOpen) }, "CC, BCC"))),
            email && (React.createElement(Editor, { wrapperClassName: "email-subject-editor-wrapper style-as-input-field", placeholder: lang.interactions.subject, toolbarHidden: true, editorState: subject, onEditorStateChange: (newEditorState) => {
                    changeSubject(newEditorState, 1000);
                }, stripPastedStyles: true, mention: props.referenceType === Entity.Case
                    ? {
                        separator: ' ',
                        trigger: '@',
                        suggestions: mentionSuggestions.map((suggestion) => ({
                            value: suggestion.label,
                            text: suggestion.label,
                            url: suggestion.label,
                        })),
                    }
                    : undefined })),
            React.createElement("br", null),
            email && (React.createElement(Editor, { ref: editorRef, editorClassName: "email-body-editor", toolbar: { options: EditorToolbarOptions }, editorState: content, onEditorStateChange: (newEditorState) => {
                    changeContent(newEditorState, 1000);
                }, stripPastedStyles: true, mention: props.referenceType === Entity.Case
                    ? {
                        separator: ' ',
                        trigger: '@',
                        suggestions: mentionSuggestions.map((suggestion) => ({
                            value: suggestion.label,
                            text: suggestion.label,
                            url: suggestion.label,
                        })),
                    }
                    : undefined })),
            React.createElement("div", { style: { marginTop: '20px' } },
                !!(uploadedFiles || [])?.length &&
                    (uploadedFiles || []).map((file, i) => (React.createElement(File, { key: file.fileId, file: file, onRemove: () => c.newInteractions.removeFile(props.referenceId, i), removable: true }))),
                React.createElement(AddFileButton, { title: lang.interactions.addFiles, onClick: () => {
                        if (dropzoneRef.current != null) {
                            // @ts-ignore
                            dropzoneRef.current?.open();
                        }
                    } }),
                React.createElement(AddFileButton, { title: lang.interactions.addTemplate, onClick: toggleAddTemplateModal }),
                !!documentsVisibleForDebtorToAdd?.length && (React.createElement(AddFileButton, { title: `${lang.interactions.addDebtorDocuments} (${documentsVisibleForDebtorToAdd.length})`, onClick: () => {
                        c.newInteractions.addFiles(props.referenceId, documentsVisibleForDebtorToAdd.map((document) => document.file));
                    } }))),
            props.isDebtor &&
                email &&
                props.referenceType === Entity.Case &&
                email.intent.type !== IntentType.Unknown && (React.createElement("div", { style: { margin: '1em 0 0 0' } },
                React.createElement(Intent, { intent: email.intent, editable: true, onEdit: (intent) => {
                        c.newInteractions.changeField(props.referenceId, 'email', 'intent', intent);
                    } }))),
            React.createElement("br", null),
            email && (React.createElement(React.Fragment, null,
                React.createElement(Button, { size: "small", loading: email.loading, disabled: !(emailToInput && hasSubject() && emailServer), onClick: sendEmail, id: "ui-test-9c459973" }, lang.interactions.sendEmail),
                ' ',
                props.referenceType === Entity.Case && (React.createElement(React.Fragment, null,
                    React.createElement(Button, { size: "small", theme: "secondary", loading: email.loading, disabled: !hasBody(), className: "ui-test-ddcdcd86", onClick: toggleEmailPreviewModal }, lang.interactions.preview),
                    React.createElement("div", { style: {
                            display: 'inline-block',
                            padding: '5px 0px 0px 16px',
                            cursor: 'pointer',
                            color: THEME.COLOR_ACCENT,
                        }, onClick: () => setVisibleToClients(!visibleToClients) }, visibleToClients ? (React.createElement(Eye, { style: {
                            height: '20px',
                            width: '20px',
                        } })) : (React.createElement(EyeOff, { style: {
                            height: '20px',
                            width: '20px',
                        } }))),
                    props.isDebtor &&
                        email &&
                        props.referenceType === Entity.Case &&
                        email.intent.type === IntentType.Unknown && (React.createElement(Button, { size: "small", theme: "secondary", loading: email.loading, className: "ui-test-ddcdcd86", onClick: () => {
                            c.newInteractions.changeField(props.referenceId, 'email', 'intent', {
                                type: IntentType.Deposit,
                                collectionPeriod: 10,
                            });
                        }, style: { float: 'right' } }, lang.interactions.addIntent))))))),
        emailTemplateAdderOpen &&
            props.referenceType === Entity.Case &&
            singleCase &&
            customer && (React.createElement(AddFromTemplate, { toggleAddTemplateModal: toggleAddTemplateModal, case: singleCase, customer: customer, mentionSuggestions: mentionSuggestions })),
        emailPreviewModalOpen &&
            props.referenceType === Entity.Case &&
            singleCase &&
            email && (React.createElement(Modal, { onToggle: toggleEmailPreviewModal, header: lang.interactions.preview },
            React.createElement(EmailPreview, { options: {
                    caseId: singleCase.caseId,
                    html: content,
                    subject: subject,
                    mentionSuggestions,
                } }),
            React.createElement("br", null),
            React.createElement(Button, { size: "small", loading: email.loading, disabled: !(emailToInput && hasSubject() && emailServer), onClick: () => {
                    sendEmail();
                    setEmailPreviewModalOpen(false);
                }, id: "ui-test-9c459973" }, lang.interactions.sendEmail))),
        mentionExplanationModalOpen && (React.createElement(Modal, { onToggle: toggleMentionExplanation, header: lang.interactions.explanation },
            React.createElement(MentionExplanation, { suggestions: mentionSuggestions })))));
};
export default EmailInteraction;
const FileStyled = styled.div `
  display: inline-flex;
  max-width: 100%;
  justify-content: space-between;
  padding: 5px 15px;
  position: relative;
  font-size: 12px;
  border-radius: 15px;
  margin-right: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  color: ${THEME.COLOR_ACCENT};
  border: 1px solid lightgrey;
  font-weight: 900;

  a {
    color: initial;
    text-decoration: none;
  }

  .file-icon {
    display: inline-block;
    margin: -2px 0 -2px 0;

    svg {
      margin: 0.2em 0em 0.2em 0em;
      transition: transform 0.2s;
      display: block;
    }

    height: 1.2em;
    width: 1.2em;
    overflow: hidden;
  }

  transition: transform 0.2s;

  &:hover {
    .file-icon {
      svg {
        transform: translate3d(0em, -1.2em, 0);
      }
    }
  }
`;
const AddFileButton = (props) => {
    return (React.createElement(FileStyled, { onClick: props.onClick },
        React.createElement("div", { style: { maxWidth: '100%' } },
            React.createElement("div", null,
                React.createElement("div", { className: "file-icon" },
                    React.createElement(PlusCircle, null),
                    React.createElement(PlusSquare, { className: "file-drag-icon" })),
                props.title))));
};
//# sourceMappingURL=EmailInteraction.js.map